
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import EditorModal from '@/components/modals/EditorModal.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import InputSwitch from 'primevue/inputswitch';
import Skeleton from 'primevue/skeleton';
import { store } from '@/store';
import { ListType } from '@/types';
import { showToastError, showToastSuccess } from '@/helpers';
import { formatNumbersInString } from '@/helpers/Number';
import { NestApiType, nestCall } from '@/composables/nestApi';

export default defineComponent({
  name: 'ListSegmentationEditor',

  components: {
    EditorModal,
    SegmentationsPanel,
    InputSwitch,
    Skeleton,
  },

  props: {
    idShopList: {
      type: Number,
      required: true,
    },
  },

  emits: ['close', 'saved'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();
    const segmentsConfiguration: any = ref(null);
    const getInitialized: any = computed(() => store.getters['segmentationsEditor/getInitialized']);

    // Local state
    const listName = ref('');
    const keepListUpdated = ref(false);
    const loadingContactsCount = ref(false);
    const totalContactsCount = ref<number | null>(null);
    const triggerCount = ref(0);

    /**
     * Fetch list details from API using nestCall
     */
    const fetchListDetails = async (): Promise<{ name: string; type: string; segmentation_config: string; keep_updated: boolean } | null> => {
      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/lists/${props.idShopList}`,
          {},
          '',
          undefined,
          true,
          { fields: ['name', 'type', 'segmentation_config', 'keep_updated'] },
        );

        if (response && response.statusCode === 200 && response.data.name
          && response.data.type === ListType.SEGMENT
        ) {
          return {
            name: response.data.name,
            type: response.data.type,
            segmentation_config: response.data.segmentation_config,
            keep_updated: response.data.keep_updated,

          };
        }
        return null;
      } catch (error) {
        console.error('Error fetching list details:', error);
        return null;
      }
    };

    /**
     * Handle count update from SegmentationsPanel
     */
    const handleCountUpdated = (data: { count: number | null; loading: boolean }) => {
      loadingContactsCount.value = data.loading;
      if (!data.loading) {
        totalContactsCount.value = data.count;
      }
    };

    /**
     * Request count refresh from SegmentationsPanel
     */
    const handleRefreshContactsCount = () => {
      loadingContactsCount.value = true;
      triggerCount.value += 1;
    };

    /**
     * Initialize the segmentation editor
     */
    const initializeEditor = async () => {
      // Fetch list details (includes type check for segment)
      const listDetails = await fetchListDetails();

      if (!listDetails) {
        await showToastError(t('listSegmentation.errors.notSegmentType'));
        router.push('/lists/manage');
        emit('close');
        return;
      }

      // Set values from API response
      listName.value = listDetails.name;
      segmentsConfiguration.value = JSON.parse(listDetails.segmentation_config || '[[{}]]');
      keepListUpdated.value = listDetails.keep_updated ?? false;
    };

    watch(getInitialized, () => {
      if (getInitialized.value) {
        handleRefreshContactsCount();
      }
    });

    /**
     * Close the editor without saving
     */
    const handleClose = () => {
      store.commit('segmentationsEditor/resetState');
      emit('close');
    };

    /**
     * Save the segmentation configuration
     */
    const handleSave = async (closeAfterSave = true) => {
      // Validate segmentation configuration
      await store.dispatch('segmentationsEditor/validate');
      const segmentationHasError = store.getters['segmentationsEditor/getHasError'];
      if (segmentationHasError) {
        await showToastError(t('listSegmentation.messages.validationError'));
        return;
      }

      store.commit('general/showTheSpinner');

      try {
        // Build the payload for the API
        const payload = {
          name: listName.value,
          segmentation_config: JSON.stringify(segmentsConfiguration.value || { conditions: [] }),
          keep_updated: keepListUpdated.value,
        };

        // Call NestJS API to update the list
        const response = await nestCall(
          'patch',
          NestApiType.CLIENT_API,
          `/lists/${props.idShopList}`,
          {},
          '',
          payload,
          true,
        );

        if (!response || response.statusCode) {
          const errorMessage = response?.message || t('listSegmentation.messages.error');
          await showToastError(errorMessage);
          return;
        }

        await showToastSuccess(t('listSegmentation.messages.saved'));
        emit('saved', props.idShopList);

        if (closeAfterSave) {
          handleClose();
        }
      } catch (error) {
        console.error('Error saving segmentation:', error);
        await showToastError(t('listSegmentation.messages.error'));
      } finally {
        store.commit('general/hideTheSpinner');
      }
    };

    // Toolbar configuration
    const toolbarRightButtons = computed(() => [
      {
        label: t('templateBuilder.save'),
        items: [
          {
            label: t('templateBuilder.saveAndLeave'),
            command: () => handleSave(true),
          },
          {
            label: t('templateBuilder.leaveWithoutSaving'),
            command: handleClose,
          },
        ],
      },
    ]);

    // Initialize on mount
    onMounted(async () => {
      await initializeEditor();
    });

    return {
      t,
      listName,
      segmentsConfiguration,
      keepListUpdated,
      toolbarRightButtons,
      loadingContactsCount,
      totalContactsCount,
      triggerCount,
      handleRefreshContactsCount,
      handleCountUpdated,
      formatNumbersInString,
    };
  },
});
